import { FacetConfiguration, FacetValueOperator } from 'components/commercetools-ui/organisms/product/product-list-algolia/types'
import {
  mapContentstackCustomFacetValues,
  withLocaleCategoryPageIdPrefix,
} from 'helpers/algolia/mapContentstackCustomFacetValues'
import { facetTemplateCurrencyCode } from 'project.config'

type ContentstackFacetItem = {
  facet: {
    active?: boolean
    attribute_name: string
    facet_type: FacetConfiguration['type']
    label: string
    label_cz?: string
    facet_value_operator?: FacetValueOperator | null
    // Contentstack reference; shape matches ContentstackAlgoliaFacetValues
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    custom_facet_values?: any
  }
}

function resolveFacetValueOperator(operator: FacetValueOperator | null | undefined): FacetValueOperator {
  return operator === 'and' ? 'and' : 'or'
}

/** Facets with `active: false` are omitted; missing `active` stays visible for backward compatibility. */
function isFacetActive(facet: ContentstackFacetItem['facet']): boolean {
  return facet.active !== false
}

export function mapContentstackFacetsConfiguration(
  rawFacets: ContentstackFacetItem[] | undefined,
  locale: string,
  currency: string,
): Record<string, FacetConfiguration> {
  const facetCurrency = facetTemplateCurrencyCode(currency)

  return (
    rawFacets?.reduce(
      (acc, { facet }) => {
        if (!isFacetActive(facet)) {
          return acc
        }

        const attributeKey = facet.attribute_name
          .replace(/\{currency\}/, facetCurrency)
          .replace(/\{locale\}/, locale)

        const mappedCustomValues =
          facet.facet_type === 'custom_term'
            ? mapContentstackCustomFacetValues(facet.custom_facet_values, locale)
            : undefined

        // Algolia indexes `categoryPageId` as locale-prefixed paths (`de-DE > Hund`).
        const customValues =
          attributeKey === 'categoryPageId' && mappedCustomValues?.length
            ? mappedCustomValues.map((entry) => ({
              ...entry,
              value: withLocaleCategoryPageIdPrefix(entry.value, locale),
            }))
            : mappedCustomValues

        return {
          ...acc,
          [attributeKey]: {
            key: attributeKey,
            type: facet.facet_type,
            label: (locale !== 'cs-CZ' ? facet.label : facet.label_cz) ?? facet.label,
            valueOperator: resolveFacetValueOperator(facet.facet_value_operator),
            ...(customValues?.length ? { customValues } : {}),
          } as FacetConfiguration,
        }
      },
      {} as Record<string, FacetConfiguration>,
    ) ?? {}
  )
}
